import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@common/auth/use-auth';
import { useNavigate } from 'react-router-dom';
import { Trans } from '@ui/i18n/trans';
import { TextField } from '@common/ui/forms/input-field/text-field/text-field';
import { Button } from '@common/ui/buttons/button';
import { Form } from '@ui/forms/form';
import { useForm, Controller } from 'react-hook-form';
import { message } from '@ui/i18n/message';
import { toast } from '@ui/toast/toast';
import { CameraAltIcon } from '@ui/icons/material/CameraAlt';
import { VideocamIcon } from '@ui/icons/material/Videocam';
import { StopCircleIcon } from '@ui/icons/material/StopCircle';
import { FileUploadIcon } from '@ui/icons/material/FileUpload';
import { FlipCameraIosIcon } from '@ui/icons/material/FlipCameraIos';
import { BusAlertIcon } from '@ui/icons/material/BusAlert';
import { FormSelect, Option } from '@ui/forms/select/select';
import { PrimitiveValue } from '@common/ui/forms/listbox/types';
const DragDropIcon = () => (
  <svg className="mx-auto w-[25px] h-[25px] text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const CheckIcon = () => (
  <svg className="w-[25px] h-[25px] text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

interface UploadFormData {
  title: string;
  description: string;
  category: string;
  location: string;
  video: File | null;
}

interface VideoDevice {
  deviceId: string;
  label: string;
  type: 'front' | 'back' | 'unknown';
}

const categoryOptions = [
  { value: 'musica', label: 'Música' },
  { value: 'danca', label: 'Dança' },
  { value: 'entretenimento', label: 'Entretenimento' },
  { value: 'esportes', label: 'Esportes' },
];

export function VideoUploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'processing' | 'success'>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoPreview, setVideoPreview] = useState<string>('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [uploadMethod, setUploadMethod] = useState<'upload' | 'record' | null>(null);
  const [videoDevices, setVideoDevices] = useState<VideoDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [videoInfo, setVideoInfo] = useState<{
    name: string;
    duration: string;
    size: string;
  } | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);

  // New state variables for video health checking
  const [isVideoHealthy, setIsVideoHealthy] = useState(true);
  const [videoError, setVideoError] = useState<string | null>(null);
  const healthCheckIntervalRef = useRef<number>();
  const lastFrameTimeRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);
  const [avgFrameRate, setAvgFrameRate] = useState<number>(0);

  const form = useForm<UploadFormData>({
    defaultValues: {
      title: '',
      description: '',
      category: '',
      location: '',
      video: null,
    },
  });

  // Function to get available video devices
  const getVideoDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices
        .filter(device => device.kind === 'videoinput')
        .map(device => {
          const deviceInfo: VideoDevice = {
            deviceId: device.deviceId,
            label: device.label || `Camera ${device.deviceId.slice(0, 4)}`,
            type: 'unknown'
          };

          // Try to determine if it's a front or back camera
          if (deviceInfo.label.toLowerCase().includes('front')) {
            deviceInfo.type = 'front';
          } else if (deviceInfo.label.toLowerCase().includes('back')) {
            deviceInfo.type = 'back';
          }

          return deviceInfo;
        });

      setVideoDevices(videoInputs);
      
      // Set default device
      if (videoInputs.length > 0 && !selectedDeviceId) {
        // Prefer back camera if available
        const backCamera = videoInputs.find(device => device.type === 'back');
        setSelectedDeviceId(backCamera?.deviceId || videoInputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error getting video devices:', error);
      toast.danger(message('Erro ao acessar as câmeras disponíveis'));
    }
  };

  // Initialize video devices when component mounts
  useEffect(() => {
    if (uploadMethod === 'record') {
      getVideoDevices();
    }
  }, [uploadMethod]);

  const formatDuration = (seconds: number) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const updateVideoInfo = (file: File, duration: number) => {
    setVideoInfo({
      name: file.name,
      duration: formatDuration(duration),
      size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
    });
  };

  // Function to stop current stream
  const stopCurrentStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };



  // NOVA FUNÇÃO
    // Function to check video stream health
    const checkVideoHealth = () => {
      if (!videoRef.current || !streamRef.current) return;
      
      const video = videoRef.current;
      const now = performance.now();
      
      // Check if video is actually playing
      if (video.readyState < 2) {
        setIsVideoHealthy(false);
        setVideoError('Erro na inicialização da câmera');
        return;
      }
  
      // Check if stream is active
      if (!streamRef.current.active) {
        setIsVideoHealthy(false);
        setVideoError('Stream de vídeo interrompido');
        return;
      }
  
      // Calculate frame rate
      frameCountRef.current++;
      const timeDiff = now - lastFrameTimeRef.current;
      
      if (timeDiff >= 1000) { // Update every second
        const fps = (frameCountRef.current * 1000) / timeDiff;
        setAvgFrameRate(Math.round(fps));
        
        // Check if frame rate is too low (below 15 fps)
        if (fps < 2) {
          setIsVideoHealthy(false);
          setVideoError('Taxa de frames muito baixa. Verifique sua câmera ou conexão');
        } else {
          setIsVideoHealthy(true);
          setVideoError(null);
        }
        
        frameCountRef.current = 0;
        lastFrameTimeRef.current = now;
      }
    };
   // Modified startRecording function
   const startRecording = async () => {
    try {
      // Reset de todos os estados relevantes
      setIsVideoHealthy(true);
      setVideoError(null);
      setIsProcessing(false);
      setVideoPreview('');
      setRecordedChunks([]); 
      form.setValue('video', null);
      setVideoInfo(null);
      
      stopCurrentStream();

      const constraints = {
        video: {
          deviceId: selectedDeviceId ? { exact: selectedDeviceId } : undefined,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { min: 2, ideal: 20 }
        },
        audio: true
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        lastFrameTimeRef.current = performance.now();
        frameCountRef.current = 0;
        
        if (healthCheckIntervalRef.current) {
          clearInterval(healthCheckIntervalRef.current);
        }
        healthCheckIntervalRef.current = window.setInterval(checkVideoHealth, 200);
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus'
      });
      
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current = null;
      }
      mediaRecorderRef.current = mediaRecorder;

      // Tipagem explícita do array de chunks
      let currentChunks: Blob[] = [];
      
      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          currentChunks.push(event.data);
          setRecordedChunks(prev => [...prev, event.data]);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessing(true);
        
        if (currentChunks.length === 0) {
          toast.danger(message('Erro na gravação: nenhum dado foi capturado'));
          setIsProcessing(false);
          return;
        }

        try {
          const blob = new Blob(currentChunks, { type: 'video/webm' });
          const videoUrl = URL.createObjectURL(blob);
          
          if (blob.size === 0) {
            toast.danger(message('Erro na gravação: arquivo vazio'));
            setIsProcessing(false);
            return;
          }

          const file = new File([blob], 'recorded-video.mp4', { type: 'video/mp4' });
          
          // Corrigindo a Promise com tipo void explícito
          await new Promise<void>((resolve, reject) => {
            const tempVideo = document.createElement('video');
            tempVideo.src = videoUrl;
            
            tempVideo.onloadedmetadata = () => {
              if (tempVideo.duration < 1) {
                toast.danger(message('Vídeo muito curto. Por favor, grave novamente.'));
                reject(new Error('Video too short'));
              } else {
                setVideoPreview(videoUrl);
                form.setValue('video', file);
                updateVideoInfo(file, tempVideo.duration);
                resolve();
              }
            };
            
            tempVideo.onerror = () => {
              reject(new Error('Error loading video'));
            };
          });
        } catch (error) {
          console.error('Error processing video:', error);
          toast.danger(message('Erro ao processar o vídeo'));
        } finally {
          setIsProcessing(false);
          stopCurrentStream();
        }
      };

      mediaRecorder.start(100);
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setIsVideoHealthy(false);
      setVideoError('Erro ao acessar a câmera. Verifique as permissões.');
      toast.danger(message('Erro ao acessar a câmera. Verifique as permissões.'));
      setIsProcessing(false);
    }
  };
  

  // New function to reset recording
  const resetRecording = () => {
    setVideoPreview('');
    setRecordedChunks([]);
    form.setValue('video', null);
    setVideoInfo(null);
    setIsRecording(false);
    setIsProcessing(false);
    stopCurrentStream();
    
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    
    if (healthCheckIntervalRef.current) {
      clearInterval(healthCheckIntervalRef.current);
    }
  };

   // Cleanup effect melhorado
   useEffect(() => {
    return () => {
      resetRecording();
    };
  }, []);


    // Modified stopRecording function
     // Modificação na função stopRecording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      if (!isVideoHealthy) {
        toast.danger(message('A gravação foi interrompida devido a problemas técnicos'));
        resetRecording();
        return;
      }
      
      // Adiciona um pequeno delay antes de parar
      setTimeout(() => {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.stop();
          setIsRecording(false);
        }
      }, 200);
    }
  };

      // Enhanced cleanup effect
  useEffect(() => {
    return () => {
      stopCurrentStream();
      if (healthCheckIntervalRef.current) {
        clearInterval(healthCheckIntervalRef.current);
      }
    };
  }, []);

  const switchCamera = async () => {
    if (isRecording) return;

    const currentIndex = videoDevices.findIndex(device => device.deviceId === selectedDeviceId);
    const nextIndex = (currentIndex + 1) % videoDevices.length;
    const nextDevice = videoDevices[nextIndex];
    
    setSelectedDeviceId(nextDevice.deviceId);
    
    if (streamRef.current) {
      stopCurrentStream();
      
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: nextDevice.deviceId } },
          audio: true
        });
        
        streamRef.current = newStream;
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
          await videoRef.current.play();
        }
      } catch (error) {
        console.error('Error switching camera:', error);
        toast.danger(message('Erro ao trocar de câmera'));
      }
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 500 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.danger(message('Arquivo muito grande. O tamanho máximo permitido é 500 MB'));
      return;
    }

    const videoUrl = URL.createObjectURL(file);
    setVideoPreview(videoUrl);
    form.setValue('video', file);

    const tempVideo = document.createElement('video');
    tempVideo.src = videoUrl;
    tempVideo.onloadedmetadata = () => {
      updateVideoInfo(file, tempVideo.duration);
    };
  };

  const handleSubmit = async (data: UploadFormData) => {
    if (!data.video || !user?.id) return;

    setUploadState('uploading');
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('video', data.video);
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('category', data.category);
    formData.append('location', data.location);
    formData.append('user_id', user.id.toString());
    
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', 'clips.php', true);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = (event.loaded / event.total) * 100;
          setUploadProgress(Math.round(progress));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          setUploadState('processing');
          setUploadProgress(100);
          
          setTimeout(() => {
            const response = JSON.parse(xhr.responseText);
            if (response.status === 'success') {
              setUploadState('success');
              toast.positive(message('Vídeo enviado com sucesso!'));
            }
          }, 1500);
        } else {
          setUploadState('idle');
          toast.danger(message('Erro ao enviar o vídeo. Tente novamente.'));
        }
      };

      // Final check before upload
    if (!isVideoHealthy && uploadMethod === 'record') {
      toast.danger(message('Não é possível enviar o vídeo devido a problemas na gravação'));
      return;
    }

      xhr.onerror = () => {
        setUploadState('idle');
        toast.danger(message('Erro na conexão. Verifique sua internet e tente novamente.'));
      };

      xhr.send(formData);
    } catch (error) {
      console.error('Erro no Upload:', error);
      setUploadState('idle');
      toast.danger(message('Erro inesperado. Tente novamente.'));
    }
  };


    // Add this to the JSX where the video preview is rendered
    const renderVideoHealthStatus = () => {
      if (!isRecording) return null;
  
      return (
        <div className={`absolute top-2 left-2 px-3 py-1 rounded-full text-sm flex items-center gap-2
          ${isVideoHealthy ? 'bg-green-500/20 text-green-700' : 'bg-red-500/20 text-red-700'}`}>
          {isVideoHealthy ? (
            <>
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span>Gravando ({avgFrameRate} FPS)</span>
            </>
          ) : (
            <>
              <BusAlertIcon className="w-[25px] h-[25px]" />
              <span>{videoError || 'Erro na gravação'}</span>
            </>
          )}
        </div>
      );
    };
  
  const resetForm = () => {
    form.reset();
    setVideoPreview('');
    setVideoInfo(null);
    setUploadState('idle');
    setUploadProgress(0);
    stopCurrentStream();
  };

  const getProgressBarColor = () => {
    if (uploadState === 'processing') return 'bg-yellow-500';
    if (uploadState === 'success') return 'bg-green-500';
    return 'bg-primary';
  };

  const getUploadStateMessage = () => {
    switch (uploadState) {
      case 'uploading':
        return `Enviando... ${uploadProgress}%`;
      case 'processing':
        return 'Processando vídeo...';
      case 'success':
        return 'Upload concluído!';
      default:
        return '';
    }
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      stopCurrentStream();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto rounded-lg shadow-sm p-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            <Trans message="Enviar Vídeo" />
          </h1>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">ID:</span>
            <span className="text-sm font-medium bg-gray-100 px-3 py-1 rounded-full">
              {user?.id}
            </span>
          </div>
        </div>

        {/* Upload Method Selection */}
        {!uploadMethod && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 py-4">
            <div
              onClick={() => setUploadMethod('upload')}
              className="relative overflow-hidden rounded-xl bg p-8
                border ring-slate-200 
                transition-all duration-300
                hover:shadow-lg hover:-translate-y-1 hover:ring-primary
                cursor-pointer group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 
                opacity-0 transition-opacity duration-300 group-hover:opacity-100" 
              />
              <div className="relative flex flex-col items-center justify-center space-y-4">
                <div className="rounded-full bg-slate-50 p-4 
                  transition-all duration-300 group-hover:bg-primary/10">
                  <FileUploadIcon className="w-[45px] h-[45px] text-slate-600 
                    transition-all duration-300 group-hover:text-primary group-hover:scale-110" 
                  />
                </div>
                <div className="text-center space-y-2">
                  <span className="block text-base font-medium text-slate-700 
                    transition-colors duration-300 group-hover:text-primary">
                    <Trans message="Fazer Upload" />
                  </span>
                  <p className="text-sm text-gray-500 
                    opacity-0 transition-all duration-300 group-hover:opacity-100">
                    Arraste ou selecione seus arquivos
                  </p>
                </div>
              </div>
            </div>

            <div
              onClick={() => setUploadMethod('record')}
              className="relative overflow-hidden rounded-xl bg p-8
                border ring-slate-200 
                transition-all duration-300
                hover:shadow-lg hover:-translate-y-1 hover:ring-primary
                cursor-pointer group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10 
                opacity-0 transition-opacity duration-300 group-hover:opacity-100" 
              />
              <div className="relative flex flex-col items-center justify-center space-y-4">
                <div className="rounded-full bg-slate-50 p-4 
                  transition-all duration-300 group-hover:bg-primary/10">
                  <CameraAltIcon className="w-[45px] h-[45px] text-slate-600 
                    transition-all duration-300 
                    group-hover:text-primary group-hover:scale-110" 
                  />
                </div>
                <div className="text-center space-y-2">
                  <span className="block text-base font-medium text-slate-700 
                    transition-colors duration-300 group-hover:text-primary">
                    <Trans message="Gravar Vídeo" />
                  </span>
                  <p className="text-sm text-gray-500 
                    opacity-0 transition-all duration-300 group-hover:opacity-100">
                    Grave diretamente da sua câmera
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {uploadMethod && (
          <Form onSubmit={handleSubmit} form={form}>
            <div className="space-y-6">
              {/* Video Recording/Upload Area */}
              <div className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-all
                ${videoPreview ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}`}
              >
                {uploadMethod === 'upload' && !videoPreview && (
                  <label htmlFor="video-upload" className="cursor-pointer block">
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/x-msvideo"
                      onChange={handleVideoChange}
                      className="hidden"
                      id="video-upload"
                      disabled={uploadState !== 'idle'}
                    />
                    <DragDropIcon />
                    <p className="mt-2 text-sm text-gray-500">Clique para carregar ou arraste e solte</p>
                    <p className="text-xs text-gray-400">MP4, MOV ou AVI (max. 500MB)</p>
                  </label>
                )}

                {uploadMethod === 'record' && !videoPreview && (
                  <div className="space-y-4">
                    {/* Camera Selection */}
                    {videoDevices.length > 1 && (
                      <div className="flex justify-center items-center space-x-4 mb-4">
                        <Button
                          variant="flat"
                          color="primary"
                          size="sm"
                          onClick={switchCamera}
                          disabled={isRecording}
                          className="flex items-center gap-2"
                        >
                          <FlipCameraIosIcon className="w-[25px] h-[25px]" />
                          <span>
                            {videoDevices.find(d => d.deviceId === selectedDeviceId)?.type === 'front' 
                              ? <Trans message="Câmera Traseira" />
                              : <Trans message="Câmera Frontal" />
                            }
                          </span>
                        </Button>
                      </div>
                    )}

                    {/* Camera Preview */}
                    <div className="relative">
                      <video
                        ref={videoRef}
                        className="max-w-full h-auto max-h-[300px] mx-auto rounded"
                        muted
                        playsInline
                      />
                      {renderVideoHealthStatus()}
                      {selectedDeviceId && (
                        <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                          {videoDevices.find(d => d.deviceId === selectedDeviceId)?.type === 'front' 
                            ? <Trans message="Câmera Frontal" />
                            : <Trans message="Câmera Traseira" />
                          }
                        </div>
                      )}
                    </div>

                    {/* Recording Controls */}
                    <Button
                        variant="flat"
                        color={isRecording ? 'danger' : 'primary'}
                        size="md"
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={isProcessing}
                        className="flex items-center justify-center gap-2"
                      >
                        {isProcessing ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <Trans message="Processando..." />
                          </>
                        ) : isRecording ? (
                          <>
                            <StopCircleIcon className="w-[25px] h-[25px]" />
                            <Trans message="Parar Gravação" />
                          </>
                        ) : (
                          <>
                            <VideocamIcon className="w-[25px] h-[25px]" />
                            <Trans message="Iniciar Gravação" />
                          </>
                        )}
                      </Button>
                  </div>
                )}

                {videoPreview && (
                  <div className="space-y-4">
                    <video
                      src={videoPreview}
                      controls
                      className="max-w-full h-auto max-h-[300px] mx-auto rounded"
                    />
                    {videoInfo && (
                      <div className="bg-gray-50 p-4 rounded-lg text-sm">
                        <p className="font-medium text-gray-700">{videoInfo.name}</p>
                        <div className="flex justify-center gap-4 text-gray-600 mt-1">
                          <span>Duração: {videoInfo.duration}</span>
                          <span>•</span>
                          <span>Tamanho: {videoInfo.size}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Form Fields */}
              <div className="grid gap-6 md:grid-cols-2">
                <TextField
                  label={<Trans message="Título" />}
                  {...form.register('title')}
                  required
                  disabled={uploadState !== 'idle'}
                  className="bg-gray-50"
                />
                   <Controller
                        name="category"
                        control={form.control}
                        render={({ field }) => (
                          <FormSelect
                            name={field.name}
                            label="Categoria"
                            className="w-full"
                            selectionMode="single"
                            selectedValue={field.value}
                            onSelectionChange={(value: PrimitiveValue) => field.onChange(value as string)}
                          >
                            {categoryOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </FormSelect>
                        )}
                      />
              </div>

              <TextField
                label={<Trans message="Descrição" />}
                {...form.register('description')}
                inputElementType="textarea"
                rows={4}
                required
                disabled={uploadState !== 'idle'}
                className="bg-gray-50"
              />

              <TextField
                label={<Trans message="Local" />}
                {...form.register('location')}
                required
                disabled={uploadState !== 'idle'}
                className="bg-gray-50"
              />

              {/* Upload Progress */}
              {uploadState !== 'idle' && (
                <div className="space-y-3">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${getProgressBarColor()} transition-all duration-300 ease-out`}
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-center text-sm font-medium text-gray-600">
                    {uploadState === 'success' && <CheckIcon />}
                    <span className="ml-2">{getUploadStateMessage()}</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                {uploadState === 'success' ? (
                  <Button
                    onClick={resetForm}
                    variant="flat"
                    color="primary"
                    size="md"
                    className="w-full flex items-center justify-center gap-2"
                  >
                    <FileUploadIcon className="w-[25px] h-[25px]" />
                    <Trans message="Enviar Outro Vídeo" />
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        resetForm();
                        setUploadMethod(null);
                      }}
                      variant="flat"
                      color="danger"
                      size="md"
                      className="flex-1"
                    >
                      <Trans message="Cancelar" />
                    </Button>
                    <Button
                      type="submit"
                      variant="flat"
                      color="primary"
                      size="md"
                      disabled={uploadState !== 'idle' || !videoPreview}
                      className="flex-1 flex items-center justify-center gap-2"
                    >
                      <FileUploadIcon className="w-[25px] h-[25px]" />
                      <Trans message="Iniciar Upload" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Form>
        )}
      </div>
    </div>
  );
}

export default VideoUploadPage;